"use client";

import Image, { ImageProps } from "next/image";
import { CSSProperties, FunctionComponent, useCallback, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import style from "./productImage.module.scss";
import NotFoundIcon from "public/next-public/common/icons/not-found.svg";

export const productImageNotFoundImagePath = "/media/dental/common/misc/not-found.png";

/**
 * Handles product images including fixing urls from the backend and providing a backup image for not-found images.
 *
 * When the source is missing or fails to load, renders the branded fallback iconmark centered and reduced
 * within the intended image footprint (rather than a full-bleed placeholder).
 *
 * Note that there is a wrapper around this component that is pre-supplied that provides an error boundry and will temporarily replace the
 * intended image with a white gif 1px wide/tall.  This typically only shows up at the first render.
 */
const ProductImage: FunctionComponent<ProductImageProps> = ({
  id,
  className,
  src: incomingSRC,
  alt,
  width,
  height,
  fallbackFillsContainer = false,
  ...rest
}) => {
  // Track the specific source that failed (not just a boolean) so a late error from a previous
  // request can't invalidate a new valid source, and a source change can't briefly flash the fallback.
  const [erroredSRC, setErroredSRC] = useState<ImageProps["src"]>();

  const onErrorCallback = useCallback(() => setErroredSRC(incomingSRC), [incomingSRC]);

  // Backend/Algolia hand imageless products a legacy server-side "not found" placeholder
  // (…/common/{misc,logos,…}/not-found.png — the old teeth asset). Treat it as a missing image so
  // the branded fallback renders instead of loading that asset, regardless of the media-server swap.
  const isNotFoundPlaceholder = typeof incomingSRC === "string" && /\/common\/[^/]+\/not-found\.png/i.test(incomingSRC);

  if (!incomingSRC || isNotFoundPlaceholder || erroredSRC === incomingSRC) {
    // The fallback is a <span>, so it has no intrinsic size the way an <img> does. By default it is
    // pinned to the requested width/height inline. Callers whose CSS sizes the image responsively
    // (e.g. the PDP media frame) opt out with fallbackFillsContainer and size it off the exposed
    // custom properties instead, so the fallback occupies the same footprint as a real image would.
    const fallbackStyle = {
      "--product-image-width": `${width}px`,
      "--product-image-height": `${height}px`,
      "--product-image-aspect-ratio": `${Number(width) / Number(height)}`,
      ...(fallbackFillsContainer ? {} : { width, height }),
    } as CSSProperties;

    return (
      <span
        id={id}
        role="img"
        aria-label={alt}
        data-product-image-fallback=""
        style={fallbackStyle}
        className={`${style.productImageFallback} ${className ?? ""}`}
      >
        <NotFoundIcon className={style.productImageFallbackIcon} aria-hidden />
      </span>
    );
  }

  return (
    <ErrorBoundary
      fallback={
        <Image
          src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="
          alt={alt}
          className={className}
          id={id}
          width={width}
          height={height}
        />
      }
    >
      <Image
        src={incomingSRC}
        alt={alt}
        className={`${style.productImageDefaultDisplay}${className ? ` ${className}` : ""}`}
        id={id}
        width={width}
        height={height}
        {...rest}
        onError={onErrorCallback}
      />
    </ErrorBoundary>
  );
};

export default ProductImage;

export type ProductImageProps = Omit<ImageProps, "onError" | "src"> &
  Partial<Pick<ImageProps, "src">> &
  Required<Pick<ImageProps, "width" | "height">> & {
    /** Let the consumer's CSS size the fallback instead of pinning it to the width/height props. */
    fallbackFillsContainer?: boolean;
  };
